// Single source of truth for which file types we accept as agent attachments.
//
// The list mirrors what OpenAI's API natively accepts as file inputs (see
// https://developers.openai.com/api/docs/guides/file-inputs) plus the image
// mime types the AI SDK forwards as `image` content parts. The composer's
// drag-and-drop / file-picker filter and the server-side `read_attachment`
// validator both import from here so they can't drift.

export type MimeKind = "image" | "file";

// Image mimes go through the AI SDK as `image` content parts.
const IMAGE_MIMES: readonly string[] = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
];

// Everything else goes through as `file` content parts. OpenAI does the
// extraction on its side (text from pptx/docx/xlsx, page rendering for pdf,
// plain text for code/markdown).
const DOCUMENT_MIMES: readonly string[] = [
  // PDF
  "application/pdf",

  // Presentations
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
  "application/vnd.ms-powerpoint", // .ppt, .pot, .ppa, .pps

  // Rich documents
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/msword", // .doc, .dot
  "application/vnd.oasis.opendocument.text", // .odt
  "application/rtf",
  "text/rtf",

  // Spreadsheets
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "application/vnd.ms-excel", // .xls, .xla, .xlb, .xlc, .xlm, .xlt, .xlw
  "text/csv",
  "application/csv",
  "text/tab-separated-values",
  "text/tsv",

  // Text & code
  "text/plain",
  "text/markdown",
  "text/html",
  "text/xml",
  "application/xml",
  "application/json",
  "text/css",
  "text/javascript",
  "application/javascript",
  "application/x-typescript",
  "text/x-python",
  "text/x-c",
  "text/x-c++",
  "text/x-java-source",
  "application/x-sh",
];

const SUPPORTED_MIMES: ReadonlySet<string> = new Set([...IMAGE_MIMES, ...DOCUMENT_MIMES]);
const IMAGE_MIME_SET: ReadonlySet<string> = new Set(IMAGE_MIMES);

// Extension → mime fallback for files where the browser didn't fill in `type`
// (drag-and-drop from Windows Explorer sometimes hands us empty strings).
const EXT_TO_MIME: Readonly<Record<string, string>> = {
  pdf: "application/pdf",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ppt: "application/vnd.ms-powerpoint",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  doc: "application/msword",
  odt: "application/vnd.oasis.opendocument.text",
  rtf: "application/rtf",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xls: "application/vnd.ms-excel",
  csv: "text/csv",
  tsv: "text/tab-separated-values",
  txt: "text/plain",
  md: "text/markdown",
  html: "text/html",
  htm: "text/html",
  xml: "text/xml",
  json: "application/json",
  css: "text/css",
  js: "text/javascript",
  ts: "application/x-typescript",
  py: "text/x-python",
  sh: "application/x-sh",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
};

export const extToMime = (filename: string): string | null => {
  const dot = filename.lastIndexOf(".");
  if (dot < 0) return null;
  const ext = filename.slice(dot + 1).toLowerCase();
  return EXT_TO_MIME[ext] ?? null;
};

export const resolveMime = (filename: string, fileType: string | undefined | null): string | null => {
  if (fileType && SUPPORTED_MIMES.has(fileType)) return fileType;
  return extToMime(filename);
};

export const isSupportedMime = (mime: string): boolean => SUPPORTED_MIMES.has(mime);

export const isImageMime = (mime: string): boolean => IMAGE_MIME_SET.has(mime);

// `accept` attribute string for <input type="file">.
export const FILE_INPUT_ACCEPT = [...SUPPORTED_MIMES].join(",");

// Convenience for the .pptx-only check on insert_deck_slides.
export const PPTX_MIME =
  "application/vnd.openxmlformats-officedocument.presentationml.presentation";
export const isPptxMime = (mime: string): boolean => mime === PPTX_MIME;
